import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCreateComunicado,
  useUpdateComunicado,
  useDeleteComunicado,
} from '@/hooks/use-comunicado-mutations';
import type { Comunicado, ComposerData } from '../types-comunicados';

type Args = {
  existing: Comunicado | undefined;
  data: ComposerData;
  setData: (d: ComposerData) => void;
};

type Mut = ReturnType<typeof useMutations>;
type Nav = ReturnType<typeof useNavigate>;
type Qc = ReturnType<typeof useQueryClient>;
type Deps = { args: Args; mut: Mut; nav: Nav; qc: Qc };

function useMutations() {
  return {
    create: useCreateComunicado(),
    update: useUpdateComunicado(),
    del: useDeleteComunicado(),
  };
}

function buildPayload(data: ComposerData, isPublished: boolean) {
  return {
    title: data.title,
    body_html: data.body_html,
    body_json: data.body_json,
    embed_urls: data.embed_urls,
    is_published: isPublished,
  };
}

function useDraftAction(d: Deps) {
  return (): void => {
    const body = buildPayload(d.args.data, false);
    if (!d.args.existing) {
      d.mut.create.mutate(body, {
        onSuccess: (res) => {
          d.args.setData({ ...d.args.data, is_published: false });
          d.nav(`/admin/communications/${res.id}/edit`);
        },
      });
      return;
    }
    d.mut.update.mutate(
      { id: d.args.existing.id, body },
      {
        onSuccess: () =>
          d.args.setData({ ...d.args.data, is_published: false }),
      }
    );
  };
}

function usePublishAction(d: Deps) {
  return (): void => {
    const body = buildPayload(d.args.data, true);
    const onSuccess = (): void => {
      d.nav('/communications');
    };
    if (!d.args.existing) {
      d.mut.create.mutate(body, { onSuccess });
      return;
    }
    d.mut.update.mutate({ id: d.args.existing.id, body }, { onSuccess });
  };
}

function useDeleteAction(d: Deps) {
  if (!d.args.existing) return undefined;
  const id = d.args.existing.id;
  return (): void => {
    if (!window.confirm('¿Eliminar este comunicado?')) return;
    d.mut.del.mutate(id, {
      onSuccess: () => {
        d.qc.invalidateQueries({ queryKey: ['comunicado', id] });
        d.nav('/admin/communications');
      },
    });
  };
}

export function useComposerPage(args: Args) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const mut = useMutations();
  const deps: Deps = { args, mut, nav, qc };
  const pending =
    mut.create.isPending || mut.update.isPending || mut.del.isPending;
  return {
    onSaveDraft: useDraftAction(deps),
    onPublish: usePublishAction(deps),
    onDelete: useDeleteAction(deps),
    pending,
  };
}
